import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { EmailOtpType } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

export default function AuthConfirmPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(
      window.location.search
    );
    const token_hash = params.get('token_hash');
    const type = params.get('type') as EmailOtpType | null;
    const next = params.get('next');

    if (!token_hash || !type) {
      setError('Invalid confirmation link.');
      return;
    }

    console.log('[AuthConfirmPage] mounted', {
      href: window.location.href,
      token_hash,
      type,
      next,
    });

    supabase.auth
      .verifyOtp({ token_hash, type })
      .then(({ error }) => {
        if (error) {
          setError(error.message);
          return;
        }

        // Default to home if `next` is absent or fails the safety check below.
        let path = '/';
        if (next) {
          try {
            // `next` may be an absolute URL (e.g. "https://commoncal.app/calendar/123?join_token=xyz")
            // or a plain path (e.g. "/calendar/123?join_token=xyz").
            // new URL() only succeeds for absolute URLs; relative paths throw and fall into the catch.
            const url = new URL(next);

            // SECURITY: open-redirect guard.
            // After verifyOtp succeeds the user is authenticated. If we blindly navigated to
            // whatever string is in `next`, an attacker could craft a magic link with
            // next=https://evil.com and steal the session. We only allow navigation to
            // paths on our own origin.
            //
            // NOTE: This is also the most likely failure point in tests/CI. If the absolute
            // URL in `next` was built with a different origin than the browser's current
            // window.location.origin (e.g. server used VITE_APP_URL=https://staging.commoncal.app
            // but the browser is at a different URL), the check silently falls through and
            // the user gets redirected to "/" instead of the intended path.
            if (url.origin === window.location.origin) {
              path = url.pathname + url.search + url.hash;
            } else {
              console.warn(
                '[AuthConfirmPage] open-redirect guard blocked navigation.',
                {
                  nextOrigin: url.origin,
                  windowOrigin: window.location.origin,
                  next,
                }
              );
            }
          } catch {
            // `next` is a relative path like "/calendar/123?join_token=xyz" — no origin to
            // check, but we still guard against protocol-relative or non-path strings.
            if (next.startsWith('/')) path = next;
          }
        }
        console.log(
          '[AuthConfirmPage] navigating to',
          path
        );
        navigate(path, { replace: true });
      });
  }, []);

  if (error) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-paper-200 px-4">
        <div className="paper-card w-full max-w-sm p-8 space-y-4">
          <p className="font-mono text-sm text-red-600">
            {error}
          </p>
          <a href="/" className="btn-ghost text-xs">
            Back to sign-in
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex items-center justify-center bg-paper-200 px-4">
      <span className="font-mono text-ink-400 text-sm">
        Signing you in…
      </span>
    </div>
  );
}
